import { useEffect, useMemo, useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate } from '@tanstack/react-router';
import type { CondicaoPagamentoResponse, GerarParcelasDto } from '@triade/contracts';
import { useAtualizarCondicaoPagamento, useCondicaoPagamento, useCriarCondicaoPagamento, useGerarParcelas, useRegenerarCondicao } from '@/api/condicoes-pagamento';
import { ErroLista } from '@/components/cargos';
import { SeloProtheus, TITULO_CAMPO_PROTHEUS, campoDoProtheus } from '@/components/integracoes-erp/selo-protheus';
import { RecordToolbar, SaidaComAlteracoesDialog, useBlockerDirty } from '@/components/record-toolbar';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDebounced } from '@/hooks/use-debounced';
import { ApiError } from '@/lib/api';
import { formatarFracaoPercentual, formatarQuantidade } from '@/lib/format';
import { cn } from '@/lib/utils';
import { CONDICAO_VAZIA, chaveParams, deCondicao, gradeGravada, paraCriar, paraRegenerar, paramsDeGeracao, TIPO_ROTULO, type CondicaoFormEntrada } from './condicao-form';

const CAMPO = 'h-[34px] w-full rounded-[9px] border border-input bg-card px-2.5 text-small text-ink-body focus-visible:border-primary focus-visible:shadow-focus focus-visible:outline-none disabled:opacity-70';
const ROTULO = 'mb-1.5 block text-caption font-medium text-ink-secondary';
const BOTAO = 'h-[34px] rounded-[10px] border border-input bg-card px-3 text-small font-semibold text-ink-body hover:bg-line-soft disabled:opacity-50';

const mensagemApi = (e: unknown) => (e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API');

export const TEXTO_DIVERGENCIA = 'Os prazos gravados diferem da regeneração pelo padrão. Corrigir é decisão sua: botão Regenerar.';

/** Registro de uma condicao (ou nova). Grade: sempre da API (/gerar ou detalhe); so a grade manual de condicao nova e digitada. */
export function CondicaoDetalhe({
  id,
  podeEditar,
  lista,
  onAviso,
}: {
  id: string | undefined;
  podeEditar: boolean;
  lista: CondicaoPagamentoResponse[];
  /** Aviso que sobrevive a troca de id (regenerar cria nova versao e a tela remonta). */
  onAviso: (texto: string) => void;
}) {
  const navigate = useNavigate();
  const doc = useCondicaoPagamento(id);
  const criar = useCriarCondicaoPagamento();
  const atualizar = useAtualizarCondicaoPagamento();
  const regenerar = useRegenerarCondicao();
  const [erroSalvar, setErroSalvar] = useState<string | null>(null);
  const [salvoEm, setSalvoEm] = useState<Date | null>(null);
  /** Id gravado (criado ou nova versao): libera a navegacao sem o aviso de alteracoes nao salvas. */
  const [idGravado, setIdGravado] = useState<string | null>(null);
  const [confirmarRegenerar, setConfirmarRegenerar] = useState(false);
  const [forcado, setForcado] = useState(false);

  const base = useMemo<CondicaoFormEntrada>(() => (doc.data ? deCondicao(doc.data) : CONDICAO_VAZIA), [doc.data]);
  const form = useForm<CondicaoFormEntrada>({ defaultValues: base, mode: 'onTouched' });
  const { fields, append, remove } = useFieldArray({ control: form.control, name: 'parcelas' });
  const { isDirty, dirtyFields } = form.formState;
  const valores = form.watch();
  const tipo = valores.tipo;

  useEffect(() => {
    if (!form.formState.isDirty) form.reset(base);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base]);

  const existente = Boolean(id && doc.data);
  const emUso = doc.data?.emUso === true;
  const importada = existente && doc.data?.tipo === null;
  /** Vem do Protheus: nome, ativa e parcelas so leitura (parcelas = tipo, quantidade, intervalo, entrada e Regenerar). */
  const doProtheus = doc.data?.doProtheus === true;
  const protheus = (campo: string) => campoDoProtheus('CondicaoPagamento', doc.data?.doProtheus, campo);
  const parcelasTravadas = protheus('parcelas');
  const manualNova = !id && tipo === 'MANUAL';
  const ehEntradaManual = tipo === 'ENTRADA_MANUAL_IGUAIS';
  const ehGerado = tipo === 'A_VISTA' || tipo === 'PARCELAS_IGUAIS' || ehEntradaManual;

  // Parametros de geracao: debounce de 300 ms com chave estavel (nao por identidade do objeto).
  const params = paramsDeGeracao(valores);
  const chave = chaveParams(params);
  const chaveAtrasada = useDebounced(chave, 300);
  const paramsAtrasados = useMemo<GerarParcelasDto | null>(() => (chaveAtrasada ? (JSON.parse(chaveAtrasada) as GerarParcelasDto) : null), [chaveAtrasada]);
  useEffect(() => setForcado(false), [chave]);

  /** Parametros iguais aos gravados: a grade e a gravada (sem nova chamada), salvo "Recalcular parcelas". */
  const mesmoGravado = existente && chave !== '' && chave === chaveParams(doc.data ? paramsDeGeracao(deCondicao(doc.data)) : null);
  const gradeDoGravado = existente && (tipo === '' || tipo === 'MANUAL' || (mesmoGravado && !forcado));
  const gerar = useGerarParcelas(gradeDoGravado ? null : paramsAtrasados);

  let grade: { days: string; pct: string }[] | undefined;
  let prazoMedio: string | undefined;
  let gradeCarregando = false;
  if (gradeDoGravado) {
    grade = gradeGravada(doc.data);
    prazoMedio = doc.data?.prazoMedio;
  } else if (paramsAtrasados !== null && chaveAtrasada === chave) {
    gradeCarregando = gerar.isPlaceholderData || gerar.isPending;
    grade = gerar.data?.parcelas;
    prazoMedio = gerar.data?.prazoMedio;
  }

  const bloqueio = useBlockerDirty(isDirty && idGravado === null);
  useEffect(() => {
    if (idGravado) void navigate({ to: '/cadastros/condicoes', search: (a) => ({ ...a, id: idGravado }), replace: true });
  }, [idGravado, navigate]);
  const salvando = criar.isPending || atualizar.isPending;
  const alterados = Object.keys(dirtyFields).length;
  const listaRota = { to: '/cadastros/condicoes' as const, search: {} };

  async function gravar(v: CondicaoFormEntrada) {
    setErroSalvar(null);
    if (!id) {
      const r = paraCriar(v);
      if (!r.ok) return setErroSalvar(r.mensagem);
      try {
        const criada = await criar.mutateAsync(r.dados);
        form.reset(deCondicao(criada));
        setSalvoEm(new Date());
        setIdGravado(criada.id);
      } catch (e) {
        setErroSalvar(mensagemApi(e));
      }
      return;
    }
    try {
      const gravada = await atualizar.mutateAsync({ id, nome: v.nome.trim(), ativa: v.ativa });
      form.reset(deCondicao(gravada));
      setSalvoEm(new Date());
      if (tipo !== (doc.data?.tipo ?? '') || !mesmoGravado) onAviso('Tipo, quantidade, intervalo e entrada só mudam por Regenerar.');
    } catch (e) {
      setErroSalvar(mensagemApi(e));
    }
  }

  async function regenerarAgora() {
    setConfirmarRegenerar(false);
    if (!id) return;
    const r = paraRegenerar(form.getValues());
    if (!r.ok) return setErroSalvar(r.mensagem);
    setErroSalvar(null);
    try {
      const nova = await regenerar.mutateAsync({ id, ...r.dados });
      form.reset(deCondicao(nova));
      setSalvoEm(new Date());
      onAviso(`Condição regenerada: ${nova.codigo}${nova.id !== id ? ' (nova versão)' : ''}.`);
      setIdGravado(nova.id);
    } catch (e) {
      setErroSalvar(mensagemApi(e));
    }
  }

  if (id && doc.isError) return <section className="rounded-2xl border border-border bg-card p-5"><ErroLista erro={doc.error} onTentar={() => void doc.refetch()} titulo="Não foi possível abrir a condição" /></section>;
  if (id && !doc.data) return <section className="rounded-2xl border border-border bg-card p-5 text-small text-ink-muted" role="status">Carregando condição…</section>;

  const condicao = doc.data;
  const versaoDe = condicao?.versaoAnteriorId ? lista.find((c) => c.id === condicao.versaoAnteriorId)?.codigo ?? 'condição anterior' : null;
  const mostraRegenerar = existente && tipo !== 'MANUAL' && !parcelasTravadas;
  const linhasEditaveis = manualNova;

  return (
    <>
      <section aria-label="Dados da condição" className="rounded-2xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-body font-semibold">{condicao ? `${condicao.codigo} · ${condicao.nome}` : 'Nova condição'}</h2>
          {doProtheus && <SeloProtheus />}
          {condicao && (
            <span className="rounded-full bg-line-soft px-2 py-0.5 text-caption font-semibold text-ink-body" data-testid="chip-tipo">
              {condicao.tipo ? TIPO_ROTULO[condicao.tipo] : 'Importada'}
            </span>
          )}
          {condicao?.prazosDivergentes && (
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0} data-testid="chip-divergentes" className="rounded-full bg-warning-bg px-2 py-0.5 text-caption font-semibold text-warning-ink">
                  prazos divergentes do padrão
                </span>
              </TooltipTrigger>
              <TooltipContent>{TEXTO_DIVERGENCIA}</TooltipContent>
            </Tooltip>
          )}
          {versaoDe && <span className="rounded-full bg-line-soft px-2 py-0.5 text-caption font-semibold text-ink-body" data-testid="chip-versao">{`versão de ${versaoDe}`}</span>}
        </div>

        {emUso && !doProtheus && (
          <p role="status" className="mt-3 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            <b>Condição em uso: parcelas imutáveis.</b> Para outra grade, regenere: será criada uma nova versão.
          </p>
        )}
        {doProtheus && (
          <p role="status" className="mt-3 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            <b>Condição do Protheus.</b> Nome, ativa e parcelas são editados no Protheus.
          </p>
        )}
        {importada && !doProtheus && (
          <p role="status" className="mt-3 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            <b>Condição importada: prazos gravados.</b> Escolha o tipo e use Regenerar para gerar a grade pela API.
          </p>
        )}
        {!podeEditar && (
          <p role="status" className="mt-3 rounded-xl border border-border bg-line-soft px-4 py-3 text-small text-ink-body">
            <b>Somente leitura.</b> Seu acesso permite consultar as condições, não alterá-las.
          </p>
        )}

        <fieldset disabled={!podeEditar} className="mt-4 grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
          <div>
            <label htmlFor="c-codigo" className={ROTULO}>Identificador</label>
            <input id="c-codigo" maxLength={40} disabled={existente} className={CAMPO} {...form.register('codigo')} />
          </div>
          <div>
            <label htmlFor="c-nome" className={ROTULO}>Nome</label>
            <input id="c-nome" maxLength={200} disabled={protheus('nome')} title={protheus('nome') ? TITULO_CAMPO_PROTHEUS : undefined} className={CAMPO} {...form.register('nome')} />
          </div>
          <div>
            <label htmlFor="c-tipo" className={ROTULO}>Tipo</label>
            <select id="c-tipo" disabled={parcelasTravadas} title={parcelasTravadas ? TITULO_CAMPO_PROTHEUS : undefined} className={CAMPO} {...form.register('tipo')}>
              {tipo === '' && <option value="">Escolha o tipo</option>}
              {(Object.keys(TIPO_ROTULO) as (keyof typeof TIPO_ROTULO)[]).map((t) => (
                <option key={t} value={t}>
                  {TIPO_ROTULO[t]}
                </option>
              ))}
            </select>
          </div>
          {(ehGerado || tipo === '') && tipo !== 'A_VISTA' && (
            <>
              <div>
                <label htmlFor="c-quantidade" className={ROTULO}>Quantidade de parcelas (conta a entrada)</label>
                <input id="c-quantidade" inputMode="numeric" disabled={parcelasTravadas} className={`${CAMPO} tabular`} {...form.register('quantidade')} />
              </div>
              <div>
                <label htmlFor="c-intervalo" className={ROTULO}>Intervalo (dias)</label>
                <input id="c-intervalo" inputMode="numeric" disabled={parcelasTravadas} className={`${CAMPO} tabular`} {...form.register('intervaloDias')} />
              </div>
              <label htmlFor="c-entrada" className="flex cursor-pointer items-center gap-2 self-end">
                <input id="c-entrada" type="checkbox" disabled={ehEntradaManual || parcelasTravadas} className="size-[18px] accent-primary" {...form.register('comEntrada')} />
                Com entrada
              </label>
            </>
          )}
          {ehEntradaManual && (
            <div>
              <label htmlFor="c-entrada-pct" className={ROTULO}>Entrada %</label>
              <input id="c-entrada-pct" inputMode="decimal" disabled={parcelasTravadas} className={`${CAMPO} tabular`} {...form.register('entradaPercentual')} />
            </div>
          )}
          <label htmlFor="c-ativa" className="flex cursor-pointer items-center gap-2 self-end">
            <input id="c-ativa" type="checkbox" disabled={protheus('ativa')} title={protheus('ativa') ? TITULO_CAMPO_PROTHEUS : undefined} className="size-[18px] accent-primary" {...form.register('ativa')} />
            Ativa
          </label>
        </fieldset>

        {podeEditar && (
          <div className="mt-4 flex flex-wrap gap-2">
            {ehGerado && !parcelasTravadas && (
              <button type="button" className={BOTAO} disabled={params === null} onClick={() => (gradeDoGravado ? setForcado(true) : void gerar.refetch())}>
                Recalcular parcelas
              </button>
            )}
            {mostraRegenerar && (
              <button type="button" className={BOTAO} onClick={() => (tipo === '' ? setErroSalvar('Escolha o tipo antes de regenerar') : setConfirmarRegenerar(true))}>
                Regenerar
              </button>
            )}
          </div>
        )}

        <div className="mt-5">
          <h4 className="mb-2 text-small font-semibold">Parcelas</h4>
          {tipo === 'MANUAL' && !id && (
            <p className="mb-2 text-caption text-ink-muted">A soma dos percentuais precisa ser 100%. A API recusa a condição se não fechar.</p>
          )}
          {linhasEditaveis && podeEditar ? (
            <table className="w-full border-collapse text-small" aria-label="Grade de parcelas">
              <thead>
                <tr className="text-left text-caption text-ink-muted">
                  <th scope="col" className="px-3 py-2 font-medium">Prazo (dias)</th>
                  <th scope="col" className="px-3 py-2 font-medium">Percentual</th>
                  <th scope="col" className="px-3 py-2 font-medium"><span className="sr-only">Ações</span></th>
                </tr>
              </thead>
              <tbody>
                {fields.map((f, i) => (
                  <tr key={f.id} data-testid="linha-parcela" className="border-t border-line-soft">
                    <td className="px-3 py-2">
                      <input aria-label={`Prazo da parcela ${i + 1}`} inputMode="numeric" className={`${CAMPO} tabular`} {...form.register(`parcelas.${i}.days`)} />
                    </td>
                    <td className="px-3 py-2">
                      <input aria-label={`Percentual da parcela ${i + 1}`} inputMode="decimal" className={`${CAMPO} tabular`} {...form.register(`parcelas.${i}.pct`)} />
                    </td>
                    <td className="px-3 py-2">
                      <button type="button" className="text-small text-danger-ink hover:underline" onClick={() => remove(i)}>Remover</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : gradeCarregando ? (
            <p role="status" className="px-1 py-3 text-small text-ink-muted">Calculando parcelas…</p>
          ) : grade ? (
            <table className="w-full border-collapse text-small" aria-label="Grade de parcelas">
              <thead>
                <tr className="text-left text-caption text-ink-muted">
                  <th scope="col" className="px-3 py-2 font-medium">Prazo (dias)</th>
                  <th scope="col" className="px-3 py-2 font-medium">Percentual</th>
                </tr>
              </thead>
              <tbody>
                {grade.map((p, i) => (
                  <tr key={i} data-testid="linha-parcela" className="border-t border-line-soft">
                    <td className="tabular px-3 py-2">{formatarQuantidade(p.days)}</td>
                    <td className="tabular px-3 py-2">{formatarFracaoPercentual(p.pct)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p className="px-1 py-3 text-small text-ink-muted">{tipo === 'ENTRADA_MANUAL_IGUAIS' ? 'Informe a entrada % para ver a grade.' : tipo === 'MANUAL' ? 'Grade manual: as linhas são gravadas como digitadas.' : 'Escolha o tipo para ver a grade.'}</p>
          )}
          {linhasEditaveis && podeEditar && (
            <button type="button" className="mt-2 text-small font-medium text-primary hover:underline" onClick={() => append({ days: '', pct: '' })}>
              Adicionar parcela
            </button>
          )}
          <p className="mt-3 text-small text-ink-secondary" data-testid="prazo-medio">
            {prazoMedio !== undefined ? `Prazo médio: ${formatarQuantidade(prazoMedio)} dias` : linhasEditaveis ? 'Prazo médio: calculado pela API ao salvar' : 'Prazo médio: —'}
          </p>
        </div>

        {erroSalvar && <p role="alert" className="mt-3 text-small text-danger-ink">{erroSalvar}</p>}
      </section>

      {podeEditar && !doProtheus && (
        <RecordToolbar
          dirty={isDirty}
          saving={salvando}
          savedAt={salvoEm}
          erro={erroSalvar}
          novo={!id}
          canPrev={false}
          canNext={false}
          alteradosCount={isDirty ? alterados : undefined}
          onList={() => void navigate({ ...listaRota, replace: true })}
          onSave={() => void form.handleSubmit(gravar, () => setErroSalvar('Corrija os campos destacados'))()}
          onNew={() => void navigate({ to: '/cadastros/condicoes', search: (a) => ({ ...a, id: 'novo' }) })}
          onUndo={() => {
            form.reset(base);
            setErroSalvar(null);
          }}
        />
      )}
      <SaidaComAlteracoesDialog {...bloqueio} />

      <AlertDialog open={confirmarRegenerar} onOpenChange={setConfirmarRegenerar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerar parcelas?</AlertDialogTitle>
            <AlertDialogDescription>
              Se a condição já foi usada em preço ou orçamento, será criada uma nova versão (código-vN); orçamentos salvos nunca mudam.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => void regenerarAgora()}>Regenerar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
